import { ToolItem } from '../types';

export interface TextToolItem extends ToolItem {
  textCategory: 'count-stats' | 'manipulation' | 'generators' | 'encode-decode' | 'markdown-code' | 'data-formatters' | 'compare-diff';
  textCategoryLabel: string;
  iconBgColor: string;
  iconTextColor: string;
  iconName: string;
}

export const TEXT_CATEGORIES_TABS = [
  { id: 'all', label: 'All Text Tools', icon: 'Type' },
  { id: 'count-stats', label: 'Counters & Stats', icon: 'BarChart2' },
  { id: 'manipulation', label: 'Text Manipulation', icon: 'Edit3' },
  { id: 'generators', label: 'Generators', icon: 'Sparkles' },
  { id: 'encode-decode', label: 'Encoders & Decoders', icon: 'Code' },
  { id: 'markdown-code', label: 'Markdown & Dev', icon: 'FileCode' },
  { id: 'data-formatters', label: 'JSON & Data Formatters', icon: 'Database' },
  { id: 'compare-diff', label: 'Compare & Diff', icon: 'GitCompare' }
];

export const TEXT_TOOLS_DATA: TextToolItem[] = [
  // --- COUNTERS & STATS (6 Tools) ---
  {
    id: 'word-counter',
    name: 'Word Counter',
    description: 'Count words, characters, sentences, and paragraphs in real-time with instant stats.',
    textCategory: 'count-stats',
    textCategoryLabel: 'Counters & Stats',
    iconBgColor: 'bg-purple-500/15',
    iconTextColor: 'text-purple-500',
    iconName: 'Hash',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'character-counter',
    name: 'Character Counter',
    description: 'Count total characters with and without spaces, including byte sizes and limit checks.',
    textCategory: 'count-stats',
    textCategoryLabel: 'Counters & Stats',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'AlignLeft',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'line-counter',
    name: 'Line Counter',
    description: 'Count non-empty lines, empty breaks, and total line density in code or raw text.',
    textCategory: 'count-stats',
    textCategoryLabel: 'Counters & Stats',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'List',
    actionType: 'generic'
  },
  {
    id: 'paragraph-counter',
    name: 'Paragraph Counter',
    description: 'Analyze total paragraph structures, average paragraph length, and block breaks.',
    textCategory: 'count-stats',
    textCategoryLabel: 'Counters & Stats',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'AlignJustify',
    actionType: 'generic'
  },
  {
    id: 'sentence-counter',
    name: 'Sentence Counter',
    description: 'Count total sentences and measure average words per sentence for readability analysis.',
    textCategory: 'count-stats',
    textCategoryLabel: 'Counters & Stats',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'MessageSquare',
    actionType: 'generic'
  },
  {
    id: 'reading-time-calculator',
    name: 'Reading Time Calculator',
    description: 'Estimate silent reading time and speaking presentation duration for articles and speech text.',
    textCategory: 'count-stats',
    textCategoryLabel: 'Counters & Stats',
    iconBgColor: 'bg-rose-500/15',
    iconTextColor: 'text-rose-500',
    iconName: 'Clock',
    actionType: 'generic'
  },

  // --- TEXT MANIPULATION (13 Tools) ---
  {
    id: 'case-converter',
    name: 'Case Converter',
    description: 'Convert text between UPPERCASE, lowercase, Title Case, camelCase, snake_case, and UPPER_CASE.',
    textCategory: 'manipulation',
    textCategoryLabel: 'Text Manipulation',
    iconBgColor: 'bg-violet-500/15',
    iconTextColor: 'text-violet-500',
    iconName: 'Type',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'remove-duplicate-lines',
    name: 'Remove Duplicate Lines',
    description: 'Deduplicate list lines, strip repetitive entries, and preserve unique rows.',
    textCategory: 'manipulation',
    textCategoryLabel: 'Text Manipulation',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'Filter',
    actionType: 'generic'
  },
  {
    id: 'sort-lines',
    name: 'Sort Lines',
    description: 'Sort text lines alphabetically (A-Z, Z-A), numerically, by line length, or randomly.',
    textCategory: 'manipulation',
    textCategoryLabel: 'Text Manipulation',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'ArrowDownAZ',
    actionType: 'generic'
  },
  {
    id: 'reverse-text',
    name: 'Reverse Text',
    description: 'Reverse character sequences, flip word orders, or reverse whole text lines upside-down.',
    textCategory: 'manipulation',
    textCategoryLabel: 'Text Manipulation',
    iconBgColor: 'bg-orange-500/15',
    iconTextColor: 'text-orange-500',
    iconName: 'RotateCcw',
    actionType: 'generic'
  },
  {
    id: 'mirror-text',
    name: 'Mirror Text',
    description: 'Create backwards flipped mirror writing and upside-down text styles for social posts.',
    textCategory: 'manipulation',
    textCategoryLabel: 'Text Manipulation',
    iconBgColor: 'bg-pink-500/15',
    iconTextColor: 'text-pink-500',
    iconName: 'Repeat',
    actionType: 'generic'
  },
  {
    id: 'shuffle-text',
    name: 'Shuffle Text',
    description: 'Randomly shuffle words, characters, or line orders to randomize lists or copy.',
    textCategory: 'manipulation',
    textCategoryLabel: 'Text Manipulation',
    iconBgColor: 'bg-cyan-500/15',
    iconTextColor: 'text-cyan-500',
    iconName: 'Shuffle',
    actionType: 'generic'
  },
  {
    id: 'text-cleaner',
    name: 'Text Cleaner',
    description: 'Strip HTML tags, weird symbols, unwanted formatting, and emojis from pasted text.',
    textCategory: 'manipulation',
    textCategoryLabel: 'Text Manipulation',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'Sparkles',
    actionType: 'generic'
  },
  {
    id: 'whitespace-remover',
    name: 'Whitespace Remover',
    description: 'Remove double spaces, trailing line spaces, tabs, and unnecessary line breaks.',
    textCategory: 'manipulation',
    textCategoryLabel: 'Text Manipulation',
    iconBgColor: 'bg-slate-500/15',
    iconTextColor: 'text-slate-400',
    iconName: 'Minimize2',
    actionType: 'generic'
  },
  {
    id: 'find-replace',
    name: 'Find Replace',
    description: 'Search and replace text strings or regular expression matches instantly across copy.',
    textCategory: 'manipulation',
    textCategoryLabel: 'Text Manipulation',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'Search',
    actionType: 'generic'
  },
  {
    id: 'text-splitter',
    name: 'Text Splitter',
    description: 'Split long paragraphs or body text into custom chunks by character length or delimiter.',
    textCategory: 'manipulation',
    textCategoryLabel: 'Text Manipulation',
    iconBgColor: 'bg-rose-500/15',
    iconTextColor: 'text-rose-500',
    iconName: 'Scissors',
    actionType: 'generic'
  },
  {
    id: 'text-joiner',
    name: 'Text Joiner',
    description: 'Join multiple lines into a single sentence separated by commas, spaces, or custom symbols.',
    textCategory: 'manipulation',
    textCategoryLabel: 'Text Manipulation',
    iconBgColor: 'bg-purple-500/15',
    iconTextColor: 'text-purple-500',
    iconName: 'Layers',
    actionType: 'generic'
  },

  // --- GENERATORS (4 Tools) ---
  {
    id: 'slug-generator',
    name: 'Slug Generator',
    description: 'Generate clean, SEO-friendly URL slugs from article titles and headlines.',
    textCategory: 'generators',
    textCategoryLabel: 'Generators',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'Link',
    actionType: 'generic'
  },
  {
    id: 'lorem-ipsum-generator',
    name: 'Lorem Ipsum Generator',
    description: 'Generate placeholder dummy text by paragraphs, sentences, words, or lists.',
    textCategory: 'generators',
    textCategoryLabel: 'Generators',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'FileText',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'random-text-generator',
    name: 'Random Text Generator',
    description: 'Create randomized alphanumeric strings, test content, and mock copywriting data.',
    textCategory: 'generators',
    textCategoryLabel: 'Generators',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'Dices',
    actionType: 'generic'
  },
  {
    id: 'password-wordlist-generator',
    name: 'Password Wordlist Generator',
    description: 'Generate combinations of memorable diceware words and custom passphrase lists.',
    textCategory: 'generators',
    textCategoryLabel: 'Generators',
    iconBgColor: 'bg-rose-500/15',
    iconTextColor: 'text-rose-500',
    iconName: 'Key',
    actionType: 'generic'
  },

  // --- ENCODERS & DECODERS (12 Tools) ---
  {
    id: 'html-encoder',
    name: 'HTML Encoder',
    description: 'Convert special characters into safe HTML entities like &amp;, &lt;, and &gt;.',
    textCategory: 'encode-decode',
    textCategoryLabel: 'Encoders & Decoders',
    iconBgColor: 'bg-purple-500/15',
    iconTextColor: 'text-purple-500',
    iconName: 'Code',
    actionType: 'generic'
  },
  {
    id: 'html-decoder',
    name: 'HTML Decoder',
    description: 'Decode escaped HTML entities back into readable plain text symbols.',
    textCategory: 'encode-decode',
    textCategoryLabel: 'Encoders & Decoders',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'FileCode',
    actionType: 'generic'
  },
  {
    id: 'url-encoder',
    name: 'URL Encoder',
    description: 'Encode string parameters into percent-encoded URI strings for web query requests.',
    textCategory: 'encode-decode',
    textCategoryLabel: 'Encoders & Decoders',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'Globe',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'url-decoder',
    name: 'URL Decoder',
    description: 'Decode percent-encoded URL links and query strings back to unescaped text.',
    textCategory: 'encode-decode',
    textCategoryLabel: 'Encoders & Decoders',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'ExternalLink',
    actionType: 'generic'
  },
  {
    id: 'base64-encoder',
    name: 'Base64 Encoder',
    description: 'Encode plain text strings into Base64 binary format.',
    textCategory: 'encode-decode',
    textCategoryLabel: 'Encoders & Decoders',
    iconBgColor: 'bg-cyan-500/15',
    iconTextColor: 'text-cyan-500',
    iconName: 'Binary',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'base64-decoder',
    name: 'Base64 Decoder',
    description: 'Decode Base64 encoded strings back into plain UTF-8 text.',
    textCategory: 'encode-decode',
    textCategoryLabel: 'Encoders & Decoders',
    iconBgColor: 'bg-sky-500/15',
    iconTextColor: 'text-sky-500',
    iconName: 'Unlock',
    actionType: 'generic'
  },
  {
    id: 'unicode-converter',
    name: 'Unicode Converter',
    description: 'Convert text to and from Unicode escape values (e.g. \\u0041) and code points.',
    textCategory: 'encode-decode',
    textCategoryLabel: 'Encoders & Decoders',
    iconBgColor: 'bg-violet-500/15',
    iconTextColor: 'text-violet-500',
    iconName: 'Cpu',
    actionType: 'generic'
  },
  {
    id: 'ascii-converter',
    name: 'ASCII Converter',
    description: 'Translate text characters to decimal ASCII codes or ASCII values back to text.',
    textCategory: 'encode-decode',
    textCategoryLabel: 'Encoders & Decoders',
    iconBgColor: 'bg-fuchsia-500/15',
    iconTextColor: 'text-fuchsia-500',
    iconName: 'Terminal',
    actionType: 'generic'
  },
  {
    id: 'binary-converter',
    name: 'Binary Converter',
    description: 'Convert text to 8-bit binary numbers (0s and 1s) and binary back to text.',
    textCategory: 'encode-decode',
    textCategoryLabel: 'Encoders & Decoders',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'Binary',
    actionType: 'generic'
  },
  {
    id: 'hex-converter',
    name: 'Hex Converter',
    description: 'Convert text characters into Hexadecimal (Hex) values and vice versa.',
    textCategory: 'encode-decode',
    textCategoryLabel: 'Encoders & Decoders',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'Hash',
    actionType: 'generic'
  },
  {
    id: 'morse-code',
    name: 'Morse Code',
    description: 'Translate plain text into International Morse Code dots (.) and dashes (-) or play audio.',
    textCategory: 'encode-decode',
    textCategoryLabel: 'Encoders & Decoders',
    iconBgColor: 'bg-orange-500/15',
    iconTextColor: 'text-orange-500',
    iconName: 'Radio',
    actionType: 'generic'
  },
  {
    id: 'rot13',
    name: 'ROT13',
    description: 'Cipher text using ROT13 letter substitution algorithm to obscure spoilers.',
    textCategory: 'encode-decode',
    textCategoryLabel: 'Encoders & Decoders',
    iconBgColor: 'bg-slate-500/15',
    iconTextColor: 'text-slate-400',
    iconName: 'Shield',
    actionType: 'generic'
  },

  // --- MARKDOWN & DEV (5 Tools) ---
  {
    id: 'markdown-preview',
    name: 'Markdown Preview',
    description: 'Live side-by-side Markdown renderer with instant HTML output visualizer.',
    textCategory: 'markdown-code',
    textCategoryLabel: 'Markdown & Dev',
    iconBgColor: 'bg-purple-500/15',
    iconTextColor: 'text-purple-500',
    iconName: 'Eye',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'markdown-editor',
    name: 'Markdown Editor',
    description: 'WYSIWYG Markdown authoring tool with syntax toolbar and clean export options.',
    textCategory: 'markdown-code',
    textCategoryLabel: 'Markdown & Dev',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'Edit3',
    actionType: 'generic'
  },
  {
    id: 'markdown-to-html',
    name: 'Markdown to HTML',
    description: 'Convert Markdown document syntax into clean, formatted HTML markup code.',
    textCategory: 'markdown-code',
    textCategoryLabel: 'Markdown & Dev',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'FileCode',
    actionType: 'generic'
  },
  {
    id: 'html-to-markdown',
    name: 'HTML to Markdown',
    description: 'Convert raw HTML code blocks back into clean readable Markdown formatting.',
    textCategory: 'markdown-code',
    textCategoryLabel: 'Markdown & Dev',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'RefreshCw',
    actionType: 'generic'
  },
  {
    id: 'regex-tester',
    name: 'Regex Tester',
    description: 'Test, debug, and validate Regular Expression patterns against test strings with live highlights.',
    textCategory: 'markdown-code',
    textCategoryLabel: 'Markdown & Dev',
    iconBgColor: 'bg-rose-500/15',
    iconTextColor: 'text-rose-500',
    iconName: 'Terminal',
    isPopular: true,
    actionType: 'generic'
  },

  // --- JSON & DATA FORMATTERS (10 Tools) ---
  {
    id: 'csv-viewer',
    name: 'CSV Viewer',
    description: 'Parse, search, filter, and view raw CSV data in an interactive tabular grid.',
    textCategory: 'data-formatters',
    textCategoryLabel: 'JSON & Data Formatters',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'Table',
    actionType: 'generic'
  },
  {
    id: 'csv-formatter',
    name: 'CSV Formatter',
    description: 'Clean, standardize delimiters, fix quote escaping, and align CSV columns.',
    textCategory: 'data-formatters',
    textCategoryLabel: 'JSON & Data Formatters',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'SlidersHorizontal',
    actionType: 'generic'
  },
  {
    id: 'tsv-viewer',
    name: 'TSV Viewer',
    description: 'View and inspect Tab-Separated Values (TSV) spreadsheet rows in clean tables.',
    textCategory: 'data-formatters',
    textCategoryLabel: 'JSON & Data Formatters',
    iconBgColor: 'bg-cyan-500/15',
    iconTextColor: 'text-cyan-500',
    iconName: 'LayoutGrid',
    actionType: 'generic'
  },
  {
    id: 'json-formatter',
    name: 'JSON Formatter',
    description: 'Format, validate, and structure raw JSON strings with custom indentation spaces.',
    textCategory: 'data-formatters',
    textCategoryLabel: 'JSON & Data Formatters',
    iconBgColor: 'bg-purple-500/15',
    iconTextColor: 'text-purple-500',
    iconName: 'Braces',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'json-validator',
    name: 'JSON Validator',
    description: 'Validate JSON syntax structure and pinpoint line number parsing errors instantly.',
    textCategory: 'data-formatters',
    textCategoryLabel: 'JSON & Data Formatters',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'CheckCircle2',
    actionType: 'generic'
  },
  {
    id: 'json-minifier',
    name: 'JSON Minifier',
    description: 'Compress and remove whitespace, tabs, and line breaks from JSON payloads.',
    textCategory: 'data-formatters',
    textCategoryLabel: 'JSON & Data Formatters',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'Minimize2',
    actionType: 'generic'
  },
  {
    id: 'json-beautifier',
    name: 'JSON Beautifier',
    description: 'Pretty-print messy minified JSON strings into human-readable trees.',
    textCategory: 'data-formatters',
    textCategoryLabel: 'JSON & Data Formatters',
    iconBgColor: 'bg-violet-500/15',
    iconTextColor: 'text-violet-500',
    iconName: 'Sparkles',
    actionType: 'generic'
  },
  {
    id: 'yaml-formatter',
    name: 'YAML Formatter',
    description: 'Validate and format YAML configuration files with proper indentation checking.',
    textCategory: 'data-formatters',
    textCategoryLabel: 'JSON & Data Formatters',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'FileText',
    actionType: 'generic'
  },
  {
    id: 'xml-formatter',
    name: 'XML Formatter',
    description: 'Format, align, and validate XML document tags with tree hierarchy views.',
    textCategory: 'data-formatters',
    textCategoryLabel: 'JSON & Data Formatters',
    iconBgColor: 'bg-rose-500/15',
    iconTextColor: 'text-rose-500',
    iconName: 'Code',
    actionType: 'generic'
  },
  {
    id: 'sql-formatter',
    name: 'SQL Formatter',
    description: 'Beautify and format messy SQL query statements with capitalized keywords and indentation.',
    textCategory: 'data-formatters',
    textCategoryLabel: 'JSON & Data Formatters',
    iconBgColor: 'bg-sky-500/15',
    iconTextColor: 'text-sky-500',
    iconName: 'Database',
    isPopular: true,
    actionType: 'generic'
  },

  // --- COMPARE & DIFF (2 Tools) ---
  {
    id: 'diff-checker',
    name: 'Diff Checker',
    description: 'Compare two text snippets side-by-side or inline to highlight additions, deletions, and edits.',
    textCategory: 'compare-diff',
    textCategoryLabel: 'Compare & Diff',
    iconBgColor: 'bg-purple-500/15',
    iconTextColor: 'text-purple-500',
    iconName: 'GitCompare',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'text-compare',
    name: 'Text Compare',
    description: 'Analyze word-by-word and line-by-line differences between two copy drafts.',
    textCategory: 'compare-diff',
    textCategoryLabel: 'Compare & Diff',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'Columns',
    actionType: 'generic'
  }
];
